var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import CakeOutlinedIcon from "@mui/icons-material/CakeOutlined";
import CelebrationOutlinedIcon from "@mui/icons-material/CelebrationOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, ButtonBase, Typography, useTheme } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TableReservationEvent } from "../../../models/reservation/reservation.model";
import { statusColor } from "./visits";
var OCCASION_COLOR = '#D9468F';
var CONNECTOR_COLOR = '#C9CED6';
var DATE_WIDTH_MAX = 110;
var SUB_ROW_INDENT = 16;
var DATE_FONT = '500 13px';
// Include sub-row indents and allow a small margin for web fonts that have not loaded yet.
export var useDateColumnWidth = function (visits) {
    var fontFamily = useTheme().typography.fontFamily;
    var key = visits.map(function (visit) { return __spreadArray([visit.dates], visit.subRows.map(function (sub) { return sub.dates; }), true).join(','); }).join('|');
    return useMemo(function () {
        var context = document.createElement('canvas').getContext('2d');
        if (!context)
            return DATE_WIDTH_MAX;
        context.font = "".concat(DATE_FONT, " ").concat(fontFamily);
        var widest = Math.max.apply(Math, __spreadArray([0], visits.flatMap(function (visit) { return __spreadArray([
            context.measureText(visit.dates).width
        ], visit.subRows.map(function (sub) { return context.measureText(sub.dates).width + SUB_ROW_INDENT; }), true); }), false));
        return Math.min(Math.ceil(widest) + 4, DATE_WIDTH_MAX);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, fontFamily]);
};
var ResourceText = function (_a) {
    var resource = _a.resource, struck = _a.struck;
    return (_jsx(Typography, __assign({ noWrap: true, title: resource.title, sx: { minWidth: 0, fontSize: 14, fontWeight: 600, color: struck ? 'text.disabled' : resource.color } }, { children: resource.text })));
};
var OccasionIcon = function (_a) {
    var occasion = _a.occasion;
    var t = useTranslation().t;
    var Icon = occasion === TableReservationEvent.BIRTHDAY ? CakeOutlinedIcon : CelebrationOutlinedIcon;
    return (_jsx(Icon, { titleAccess: t("Restaurant event ".concat(occasion)), sx: { fontSize: 15, flexShrink: 0, color: OCCASION_COLOR } }));
};
var titleWhenCut = function (event) {
    var _a;
    var element = event.currentTarget;
    element.title = element.scrollWidth > element.clientWidth ? (_a = element.textContent) !== null && _a !== void 0 ? _a : '' : '';
};
var Row = function (_a) {
    var visit = _a.visit, dateWidth = _a.dateWidth, sub = _a.sub, SectorIcon = _a.sectorIcon;
    var struckSx = visit.struck ? { textDecoration: 'line-through', color: 'text.disabled' } : {};
    return (_jsxs(Box, __assign({ sx: {
            display: 'flex',
            gap: '10px',
            alignItems: 'center',
            height: sub ? 30 : 34,
            pl: '10px',
            pr: '6px',
            borderLeft: '5px solid',
            borderLeftColor: statusColor(visit.status),
            opacity: visit.past ? 0.55 : 1
        } }, { children: [_jsx(Typography, __assign({ noWrap: true, sx: __assign({ width: dateWidth - (sub ? SUB_ROW_INDENT : 0), flexShrink: 0, fontSize: 13, fontWeight: 500, color: 'text.secondary' }, struckSx) }, { children: visit.dates })), _jsxs(Box, __assign({ sx: { flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '10px' } }, { children: [_jsxs(Box, __assign({ sx: { flex: '1 1 0', minWidth: 0, display: 'flex', alignItems: 'center', gap: '5px' } }, { children: [SectorIcon && _jsx(SectorIcon, { sx: { fontSize: 16, flexShrink: 0, mr: '1px', color: 'text.secondary' } }), _jsx(Typography, __assign({ noWrap: true, title: visit.whatTitle, onMouseEnter: visit.whatTitle ? undefined : titleWhenCut, sx: __assign({ fontSize: sub ? 14 : 15 }, struckSx) }, { children: visit.what })), visit.occasion && _jsx(OccasionIcon, { occasion: visit.occasion })] })), visit.label && (_jsx(Box, __assign({ sx: { display: 'inline-flex', maxWidth: '50%', minWidth: 0 } }, { children: _jsx(ResourceText, { resource: visit.label, struck: visit.struck }) })))] }))] })));
};
export var VisitRow = function (_a) {
    var visit = _a.visit, dateWidth = _a.dateWidth, sectorIcon = _a.sectorIcon;
    return (_jsxs(_Fragment, { children: [_jsx(Row, { visit: visit, dateWidth: dateWidth, sectorIcon: sectorIcon }), visit.subRows.length > 0 && (_jsx(Box, __assign({ sx: {
                    position: 'relative',
                    ml: "".concat(SUB_ROW_INDENT, "px"),
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px',
                    // One line for the whole group (never per row, or faded rows would fade it), down to the last tick.
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: '-9px',
                        top: '-4px',
                        bottom: '15px',
                        borderLeft: "1.5px solid ".concat(CONNECTOR_COLOR)
                    }
                } }, { children: visit.subRows.map(function (sub) { return (_jsxs(Box, __assign({ sx: { position: 'relative' } }, { children: [_jsx(Box, { sx: { position: 'absolute', left: '-9px', top: '14px', width: '9px', borderTop: "1.5px solid ".concat(CONNECTOR_COLOR) } }), _jsx(Row, { visit: sub, dateWidth: dateWidth, sub: true })] }), sub.id)); }) })))] }));
};
export var GroupTitle = function (_a) {
    var children = _a.children, current = _a.current, folded = _a.folded, onToggle = _a.onToggle;
    var sx = {
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
        alignSelf: 'flex-start',
        py: '2px',
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: current ? 'primary.main' : 'text.secondary',
        fontFamily: 'inherit'
    };
    if (!onToggle) {
        return _jsx(Typography, __assign({ component: 'div', sx: sx }, { children: children }));
    }
    return (_jsxs(ButtonBase, __assign({ onClick: onToggle, "aria-expanded": !folded, sx: __assign(__assign({}, sx), { borderRadius: '4px', pr: '2px' }) }, { children: [children, folded ? _jsx(ChevronRightIcon, { sx: { fontSize: 18 } }) : _jsx(ExpandMoreIcon, { sx: { fontSize: 18 } })] })));
};
